import { NavLink, Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { LayoutDashboard, Users, Inbox, HandshakeIcon, LogOut, ExternalLink, Calendar, Archive, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const nav = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard, tid: "nav-dashboard" },
  { to: "/admin/events", label: "Events", icon: Calendar, tid: "nav-events" },
  { to: "/admin/competitors", label: "Competitors", icon: Users, tid: "nav-competitors" },
  { to: "/admin/archive", label: "Archive", icon: Archive, tid: "nav-archive" },
  { to: "/admin/submissions", label: "Inquiries", icon: Inbox, tid: "nav-submissions" },
  { to: "/admin/sponsors", label: "Sponsors", icon: HandshakeIcon, tid: "nav-sponsors-admin" },
  { to: "/admin/accounts", label: "Accounts", icon: Users, tid: "nav-admin-accounts" },
  { to: "/admin/access", label: "Admin Access", icon: ShieldCheck, tid: "nav-admin-access" },
];

export default function AdminLayout() {
  const { user, logout, checkAuth } = useAuth();
  const location = useLocation();
  useEffect(() => { checkAuth(); }, [location.pathname, checkAuth]);
  useEffect(() => {
    const recheck = () => { if (document.visibilityState === "visible") checkAuth(); };
    window.addEventListener("focus", recheck);
    document.addEventListener("visibilitychange", recheck);
    return () => { window.removeEventListener("focus", recheck); document.removeEventListener("visibilitychange", recheck); };
  }, [checkAuth]);
  const navigate = useNavigate();
  const onLogout = async () => { await logout(); navigate("/admin/login"); };
  return (
    <div className="min-h-screen bg-[#FDFFFC] flex">
      <aside className="w-64 shrink-0 border-r border-slate-100 bg-white sticky top-0 h-screen flex flex-col">
        <div className="p-6 border-b border-slate-100">
          <Link to="/admin" className="flex items-center gap-3">
            <img src="/assets/logo.png" alt="Naptown Cubers" className="h-10 w-auto" />
            <span className="text-[10px] uppercase tracking-wider text-[#7A7D8C]">Organizer Console</span>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {nav.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.tid}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${isActive ? "bg-[#FF9F1C] text-white shadow-[0_6px_16px_rgb(255,159,28,0.28)]" : "text-[#1E1E24] hover:bg-[#FFBF69]/15"}`}
              >
                <Icon className="w-4 h-4" /> {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-4 border-t border-slate-100 space-y-3">
          <Link to="/" className="flex items-center gap-2 text-xs text-[#7A7D8C] hover:text-[#FF9F1C]"><ExternalLink className="w-3.5 h-3.5" /> View public site</Link>
          <div className="flex items-center gap-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-[#FFBF69]/30 grid place-items-center text-xs font-bold text-[#B36906]">{(user?.name || "?")[0]}</div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate" data-testid="admin-user-name">{user?.name || "Organizer"}</div>
              <div className="text-[11px] text-[#7A7D8C] truncate">{user?.email}</div>
            </div>
            <button onClick={onLogout} data-testid="logout-btn" className="p-2 rounded-lg hover:bg-slate-100 text-[#7A7D8C]" title="Sign out">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>
      <main className="flex-1 min-w-0 p-8">
        <Outlet />
      </main>
    </div>
  );
}
